import { defineStore } from "pinia";
import { ref, watch } from "vue";
import { Configuration, ThreadsApi, type MessageThread, type Message } from "@/api/javaapi";
import { useAuthStore } from "@/stores/auth.store";

export const useThreadStore = defineStore("thread", () => {
  const authStore = useAuthStore();
  const threadApi = new ThreadsApi(
    new Configuration({
      accessToken: (name?: string, scopes?: string[]) => authStore.token ?? ""
    })
  );

  const threadList = ref<MessageThread[]>([]);
  const openThreads = ref<Map<number, { 
    thread: MessageThread | null; 
    count: number;
    lastMessageId: number | null;
  }>>(new Map());

  let pollingTimeoutId: number | null = null;
  const activePollInterval = 5000; // 5 seconds when window is active
  const inactivePollInterval = 600000; // 10 minutes when window is inactive
  let currentPollInterval = document.visibilityState === "visible" ? activePollInterval : inactivePollInterval;

  // Reset store state on logout
  watch(() => authStore.token, (newToken, oldToken) => {
    if (oldToken && !newToken) { // Logout detected
      threadList.value = [];
      openThreads.value.clear();
      if (pollingTimeoutId !== null) {
        clearTimeout(pollingTimeoutId);
        pollingTimeoutId = null;
      }
    }
  });

  async function fetchNewMessages(threadIds: number[], lastMessageIds: (number | null)[]): Promise<Message[]> {
    try {
      const threadIdsStr = threadIds.join(",");
      const lastMessageIdsStr = lastMessageIds.map(id => id ?? 0).join(",");
      const response = await threadApi.getNewMessages(threadIdsStr, lastMessageIdsStr);
      return response.data as Message[];
    } catch (error) {
      console.error("Failed to fetch new messages:", error);
      return [];
    }
  }

  async function fetchThreadList(offset: number, size: number, search: string): Promise<number> {
    try {
      const response = await threadApi.getThreadList(offset, size, search);
      const newThreads = response.data.threads ?? [];
      if (offset === 0) {
        threadList.value = newThreads;
      } else {
        threadList.value = [...threadList.value, ...newThreads];
      }
      return response.data.totalThreadCount ?? 0;
    } catch (error) {
      console.error("Failed to fetch thread list:", error);
      threadList.value = offset === 0 ? [] : threadList.value;
      return 0;
    }
  }

  function updateThreadInList(updatedThread: MessageThread) {
    if (updatedThread.ID === undefined) return;
    const index = threadList.value.findIndex((t) => t.ID === updatedThread.ID);
    if (index !== -1) {
      threadList.value[index] = { ...updatedThread };
    } else {
      threadList.value.push(updatedThread);
    }
    if (openThreads.value.has(updatedThread.ID)) {
      openThreads.value.get(updatedThread.ID)!.thread = updatedThread;
    }
  }

  async function openThread(threadId: number): Promise<MessageThread | undefined> {
    const entry = openThreads.value.get(threadId);
    if (entry) {
      entry.count++;
      return entry.thread ?? undefined;
    }

    try {
      // Go ahead and set the openThread in case this gets called a 2nd time while loading.
      openThreads.value.set(threadId, { thread: null, count: 1, lastMessageId: 0 });
      const response = await threadApi.getThread(threadId);
      const thread = response.data;
      openThreads.value.get(threadId)!.thread = thread;
      openThreads.value.get(threadId)!.lastMessageId = thread.messages?.length ? Math.max(...thread.messages.map(m => m.id ?? 0)) : 0;
      startPolling();
      return thread;
    } catch (error) {
      console.error(`Failed to fetch thread ${threadId}:`, error);
      return undefined;
    }
  }

  function closeThread(threadId: number) {
    const entry = openThreads.value.get(threadId);
    if (entry) {
      entry.count--;
      if (entry.count <= 0) {
        openThreads.value.delete(threadId);
      }
    }

    if (openThreads.value.size === 0 && pollingTimeoutId !== null) {
      clearTimeout(pollingTimeoutId);
      pollingTimeoutId = null;
    }
  }

  function getOpenThread(threadId: number): MessageThread | null | undefined {
    return openThreads.value.get(threadId)?.thread;
  }

  async function poll() {
    if (openThreads.value.size === 0) return;
  
    const threadIds = Array.from(openThreads.value.keys());
    const lastMessageIds = Array.from(openThreads.value.values()).map(entry => entry.lastMessageId);
  
    const newMessages = await fetchNewMessages(threadIds, lastMessageIds);
  
    const messagesByThread = new Map<number, Message[]>();
    newMessages.forEach(message => {
      if (message.threadID !== undefined) {
        const threadMessages = messagesByThread.get(message.threadID) || [];
        threadMessages.push(message);
        messagesByThread.set(message.threadID, threadMessages);
      }
    });
  
    for (const [threadId, newMessages] of messagesByThread) {
      const entry = openThreads.value.get(threadId);
      if (entry && entry.thread && newMessages.length > 0) {
        const existingMessages = entry.thread.messages ?? [];
        entry.thread.messages = [
          ...existingMessages,
          ...newMessages.filter(newMsg => 
            !existingMessages.some(existingMsg => existingMsg.id === newMsg.id)
          )
        ].sort((a, b) => {
          const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
          const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
          return timeA - timeB;
        });
        
        entry.lastMessageId = newMessages.length > 0 ? Math.max(...newMessages.map(m => m.id ?? 0)) : entry.lastMessageId ?? 0;
        updateThreadInList(entry.thread);
      }
    }
    
    pollingTimeoutId = setTimeout(poll, currentPollInterval);
  }

  function startPolling() {
    if (pollingTimeoutId !== null) return; // Already scheduled
    pollingTimeoutId = setTimeout(poll, currentPollInterval);
  }

  function updatePollingInterval() {
    const oldInterval = currentPollInterval;
    currentPollInterval = document.visibilityState === "visible" ? activePollInterval : inactivePollInterval;
    if (pollingTimeoutId !== null && oldInterval !== currentPollInterval) {
      clearTimeout(pollingTimeoutId);
      poll();
    }
  }

  document.addEventListener("visibilitychange", updatePollingInterval);

  return {
    threadList,
    openThreads,
    fetchThreadList,
    updateThreadInList,
    openThread,
    closeThread,
    getOpenThread,
  };
});